import classNames from 'classnames'
import AiMark from '@/components/ui/ai/AiMark'

// Valeurs d'évaluation → [label, ton]. `null` = on n'affiche pas (trop neutre).
const SENTIMENT = {
    positive: ['Positif', 'green'],
    neutral: null,
    negative: ['Négatif', 'red'],
}
const FRUSTRATION = {
    none: null,
    mild: ['Agacé', 'amber'],
    high: ['Très frustré', 'red'],
}
const SATISFACTION = {
    satisfied: ['Satisfait', 'green'],
    mixed: ['Mitigé', 'amber'],
    unsatisfied: ['Insatisfait', 'red'],
    unknown: null,
}
const RESOLUTION = {
    resolved: ['Résolu', 'green'],
    pending: ['En cours', 'blue'],
    unresolved: ['Non résolu', 'gray'],
}

const TONE = {
    green: 'bg-green-50 text-green-700',
    red: 'bg-red-50 text-red-700',
    amber: 'bg-amber-50 text-amber-700',
    blue: 'bg-blue-50 text-blue-700',
    gray: 'bg-zinc-200/70 text-zinc-600',
}

const Pill = ({ label, tone }) => (
    <span
        className={classNames(
            'text-[11px] px-2 py-0.5 rounded-full whitespace-nowrap',
            TONE[tone] || TONE.gray
        )}
    >
        {label}
    </span>
)

// Map une valeur d'éval vers une pill {label, tone}, ou null si absente/neutre.
const toPill = (map, value) => {
    const entry = value && map[value]
    return entry ? { label: entry[0], tone: entry[1] } : null
}

/**
 * Analyse IA d'un échange Gorgias : problème du client + signaux d'évaluation
 * (sentiment, frustration, satisfaction, résolution).
 * Réutilisable : fil de conversation, page service case, inbox support…
 */
export default function GorgiasAiSummary({ ai, title = 'Analyse IA', className }) {
    if (!ai?.abstract) return null

    const { abstract, sentiment, frustration, satisfaction, resolution, topics = [] } = ai

    const pills = [
        toPill(SENTIMENT, sentiment),
        toPill(FRUSTRATION, frustration),
        toPill(SATISFACTION, satisfaction),
        toPill(RESOLUTION, resolution),
    ].filter(Boolean)

    return (
        <div
            className={classNames(
                'rounded-xl border border-zinc-200 bg-zinc-100 px-4 py-3.5',
                className
            )}
        >
            <div className="flex items-center gap-2 mb-2">
                <AiMark size="lg" />
                <span className="font-medium text-zinc-900">{title}</span>
                {pills.length > 0 && (
                    <div className="flex flex-wrap gap-1 ml-auto justify-end">
                        {pills.map((p, i) => (
                            <Pill key={i} label={p.label} tone={p.tone} />
                        ))}
                    </div>
                )}
            </div>

            <p className="text-sm text-zinc-600 leading-relaxed">{abstract}</p>

            {/* Sujets précis détectés (registre ticket_topics) — distincts des
                signaux d'éval : ce sont les motifs récurrents de la demande. */}
            {topics.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2.5">
                    {topics.map((t) => (
                        <span
                            key={t.slug}
                            title={t.slug}
                            className="text-[11px] px-2 py-0.5 rounded-full whitespace-nowrap bg-white border border-zinc-200 text-zinc-600"
                        >
                            {t.label}
                        </span>
                    ))}
                </div>
            )}
        </div>
    )
}
